// ── the netti space's views, and who may see the one that is only on a mac ────
//
// "you & netti", "appearance" and "settings" are for everyone. "on your mac" is
// netti's own settings on this computer (the corner, its sounds and voice, what
// it may see and do): it is the Mac app's, and for now the founders' (F.nettiDesk).
// So the chip exists only when BOTH are true: the page is inside the app (its
// bridge answers deskStatus) and the person can see the feature. Anywhere else a
// pick of that view is ignored, so a link from the browser's ask netti, or an old
// bookmark, lands on the first view instead of an empty room.
//
// IDENTITIES ARE NOT HERE ANY MORE (John, 25 oct: "change it to identities and keep
// it in the agent space"). From 18 to 24 oct the netti space had a view whose id was
// `identity` (chip "personalities"): how the person wants every ai to talk, answer
// and behave, and what netti noticed they asked of one (lib/identity). It moved into
// the agents space as the view "identities" (components/agents/agentViews), for the
// same few (F.agentIdentity, founders for now, inside F.agents). Links to the old
// place are still out there (netti ?v=identity, a tour or a chat link naming the
// panel view `netti` / `identity`, PANEL_VIEW_IDS in lib/netti/linkActions), so
// `movedNettiView` says where each one goes now, and DashboardClient takes it there.
// The ids in the old table are internal and stay; only the room changed.
//
// The gate looks at the bridge object and never calls it: a status read from a
// visible window ticks the setup walk's "settings:opened" step, and the chip row
// must not check that off the moment anyone opens the netti space.
//
// Pure: no window, no React. NettiSpace and DashboardClient ask these.

export type NettiView = "you" | "appearance" | "settings" | "digest" | "mac";

/** every view the space has, in the order its chips stand. "daily digest" moved here from settings
 *  (John, 2026-10-27), for everyone since 10-28. */
export const NETTI_VIEW_IDS: readonly NettiView[] = ["you", "appearance", "settings", "digest", "mac"];

/** the carrier a pick from outside leaves for a space that has not mounted yet (lib/netti/linkActions PANEL_VIEW_IDS holds the ids) */
export const NETTI_PANEL_VIEW_KEY = "innernet:panel-view:netti";

const LABELS: Record<NettiView, string> = {
  you: "you & netti",
  appearance: "appearance",
  settings: "settings",
  digest: "daily digest",
  mac: "on your mac",
};

/** is this page inside the Mac app, and does the app know netti's corner (a build from before it has no deskStatus)? */
export function inMacApp(bridge: unknown): boolean {
  return !!bridge && typeof (bridge as { deskStatus?: unknown }).deskStatus === "function";
}

/** does the "on your mac" view exist here? Only inside the app, and only for who can see netti on the desk. */
export function macViewShown(deskOpen: boolean, bridge: unknown): boolean {
  return deskOpen && inMacApp(bridge);
}

/** the chips of the space: the mac's last and only where it exists (never a gap, never a reshuffle: it is
 *  known before the row first paints or appended after) */
export function nettiChips(mac: boolean, digest = false): Array<{ id: NettiView; label: string }> {
  return NETTI_VIEW_IDS.filter((id) => (id !== "mac" || mac) && (id !== "digest" || digest)).map((id) => ({ id, label: LABELS[id] }));
}

/** a view named from outside (the address's ?v=, a pick), or null when it is none of ours */
export function nettiViewOf(v: unknown): NettiView | null {
  return typeof v === "string" && (NETTI_VIEW_IDS as readonly string[]).includes(v) ? (v as NettiView) : null;
}

/** the view a pick lands on, or null to ignore it: "on your mac" only where the space has it */
export function takeNettiView(v: unknown, mac: boolean, digest = false): NettiView | null {
  const id = nettiViewOf(v);
  if (id === "mac" && !mac) return null;
  if (id === "digest" && !digest) return null;
  return id;
}

/**
 * The view the server's address names (`/dashboard/netti?v=mac`): only for the
 * netti place itself, so a `?v=` meant for another place never reaches here.
 * It is the first view's name until the page knows whether it can show the mac's.
 */
export function arrivalView(place: string | null | undefined, v: string | null | undefined): NettiView | null {
  return place === "/netti" ? nettiViewOf(v) : null;
}

/**
 * settings → netti on the desk used to be a tab of settings, and links to it are
 * still out there (an app built before the move picks it; a bookmark; the mirror).
 * Someone who can see netti's corner is taken to the netti space's mac view; anyone
 * else lands where the old tab fell back to, the app tab.
 */
export function retiredDeskTab(deskOpen: boolean): { panel: "netti"; view: "mac" } | { panel: "settings"; view: "app" } {
  return deskOpen ? { panel: "netti", view: "mac" } : { panel: "settings", view: "app" };
}

/** where a view the netti space used to have lives now: the panel and the view it opens there */
export interface MovedView {
  panel: "agents";
  view: "identities";
}

/**
 * The netti space's old "identity" view (chip "personalities", 18 to 24 oct) is the agents space's
 * "identities" view now. A pick of it from anywhere (the address's ?v=identity, a link netti gave, a tour,
 * MovedIn's door, a bookmark) goes there for whoever can see it, and nowhere for anyone else: for them there
 * was never such a view, so the pick is ignored and the space lands on its first view. `identityOpen` is
 * F.agentIdentity AND the agents space itself (DashboardClient says both); the agents space is the room.
 */
export function movedNettiView(v: unknown, identityOpen: boolean): MovedView | null {
  return v === "identity" && identityOpen ? { panel: "agents", view: "identities" } : null;
}

/**
 * The view the server's address names for a place that no longer has it (`/dashboard/netti?v=identity`):
 * only for the netti place itself, so a `?v=` meant for another place never reaches here.
 */
export function arrivalMoved(place: string | null | undefined, v: string | null | undefined, identityOpen: boolean): MovedView | null {
  return place === "/netti" ? movedNettiView(v, identityOpen) : null;
}

/**
 * settings → agent identity was a tab of settings until it moved to the netti space (20 oct) and then to the
 * agents space (25 oct), and a pick of it is still out there (a link netti gave, a bookmark, a tour).
 * Someone who can see agent identity is taken to the agents space's identities view; for anyone else there
 * was never such a tab, so the pick is ignored.
 */
export function retiredIdentityTab(identityOpen: boolean): MovedView | null {
  return identityOpen ? { panel: "agents", view: "identities" } : null;
}
